/**
 * User Activity page — summary tiles, side-by-side horizontal bar charts,
 * and a paged user table.
 */
import { useCallback, useMemo, useState } from 'react';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import Button from '@/components/Button';
import {
  useActivityDaily,
  useActivitySummary,
  useActivityTopCountries,
  useActivityTopDocuments,
  useActivityTopEvents,
  useActivityTopPages,
  useActivityUserDetail,
  useActivityUsers,
} from './api';
import { formatNumber } from './lib/utils';
import { formatDateTime } from '@/lib/utils/dates';
import { useDebouncedSearch, useUrlParams } from './lib/hooks';
import { buildActivityDailyChart, buildHBar } from './lib/charts';
import ActivityUserDetailModalBody from './components/ActivityUserDetailModalBody';
import { LoaderRow, SummaryCard } from './components/parts';
import type { ActivityUsersSortKey, SortOrder } from './types';

const PAGE_SIZE = 50;
const DAY_OPTIONS = [7, 14, 30, 60, 90];
const DEFAULT_DAYS = 30;

const COLUMNS: { key: ActivityUsersSortKey | 'actions'; label: string; sortable: boolean }[] = [
  { key: 'email', label: 'Email', sortable: true },
  { key: 'event_count', label: 'Events', sortable: true },
  { key: 'page_views', label: 'Page Views', sortable: true },
  { key: 'doc_views', label: 'Docs Viewed', sortable: false },
  { key: 'last_active', label: 'Last Active', sortable: true },
  { key: 'actions', label: 'Actions', sortable: false },
];

export default function UserActivityPage(): JSX.Element {
  const { params, update } = useUrlParams();

  const page = Math.max(0, parseInt(params.get('page') ?? '0', 10) || 0);
  const search = params.get('q') ?? '';
  const sortBy = (params.get('sort') ?? 'last_active') as ActivityUsersSortKey;
  const order = (params.get('order') ?? 'desc') as SortOrder;
  const days = parseInt(params.get('days') ?? String(DEFAULT_DAYS), 10) || DEFAULT_DAYS;

  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);

  const onSearchCommit = useCallback(
    (value: string) => update({ q: value || null, page: 0 }),
    [update],
  );
  const searchInput = useDebouncedSearch(search, 300, onSearchCommit);

  const summaryQ = useActivitySummary();
  const dailyQ = useActivityDaily(days);
  const topPagesQ = useActivityTopPages(30);
  const topEventsQ = useActivityTopEvents(30);
  const topDocumentsQ = useActivityTopDocuments(30);
  const topCountriesQ = useActivityTopCountries(30);
  const usersQ = useActivityUsers({
    sortBy,
    order,
    search,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });
  const userDetailQ = useActivityUserDetail(selectedEmail);

  const onSort = (key: ActivityUsersSortKey): void => {
    if (sortBy === key) {
      update({ order: order === 'desc' ? 'asc' : 'desc', page: 0 });
    } else {
      update({ sort: key, order: 'desc', page: 0 });
    }
  };

  const summary = summaryQ.data;
  const mostActive = summary?.most_active_user
    ? `${summary.most_active_user.split('@')[0]} (${formatNumber(summary.most_active_count)})`
    : '-';

  const dailyChart = useMemo(() => buildActivityDailyChart(dailyQ.data ?? []), [dailyQ.data]);
  const topPagesChart = useMemo(
    () =>
      buildHBar(
        topPagesQ.data ?? [],
        (d) => d.page_path,
        (d) => d.views,
        'Views',
        '#4EB7AC',
      ),
    [topPagesQ.data],
  );
  const topEventsChart = useMemo(
    () =>
      buildHBar(
        topEventsQ.data ?? [],
        (d) => d.event_name,
        (d) => d.count,
        'Count',
        '#1D3A47',
      ),
    [topEventsQ.data],
  );
  const topDocumentsChart = useMemo(
    () =>
      buildHBar(
        topDocumentsQ.data ?? [],
        (d) =>
          d.description ||
          [d.bank, d.document_type].filter(Boolean).join(' — ') ||
          'Unknown document',
        (d) => d.opens,
        'Opens',
        '#4EB7AC',
      ),
    [topDocumentsQ.data],
  );
  const topCountriesChart = useMemo(
    () =>
      buildHBar(
        topCountriesQ.data ?? [],
        (d) => d.country ?? 'Unknown',
        (d) => d.selections,
        'Selections',
        '#1D3A47',
      ),
    [topCountriesQ.data],
  );

  const total = usersQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-[1200px] px-4 py-6">
      <PageHeader
        title="User Activity"
        subtitle="Behavioral tracking and content engagement analytics"
      />

      <section className="mb-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard label="Active Users" value={formatNumber(summary?.active_users)} />
          <SummaryCard label="Total Events" value={formatNumber(summary?.total_events)} />
          <SummaryCard label="Events Today" value={formatNumber(summary?.events_today)} highlight />
          <SummaryCard label="Users Today" value={formatNumber(summary?.users_today)} />
          <SummaryCard label="Events This Week" value={formatNumber(summary?.events_this_week)} />
          <SummaryCard label="Users This Week" value={formatNumber(summary?.users_this_week)} />
          <SummaryCard label="Top Page (30d)" value={summary?.top_page ?? '-'} />
          <SummaryCard label="Most Active (30d)" value={mostActive} />
        </div>
      </section>

      <Card
        className="mb-8 p-6"
        title="Daily Activity"
        actions={
          <select
            className="rounded-md border border-[color:var(--gray-200)] bg-white px-2 py-1 text-sm font-[var(--font-body)] text-[color:var(--text-dark)]"
            value={days}
            onChange={(e) => update({ days: parseInt(e.target.value, 10) })}
          >
            {DAY_OPTIONS.map((d) => (
              <option key={d} value={d}>
                Last {d} days
              </option>
            ))}
          </select>
        }
      >
        <div className="mt-4" style={{ height: 320 }}>
          {dailyQ.isError ? (
            <p className="p-8 text-center text-sm text-[color:var(--text-dark)]">
              Failed to load chart data
            </p>
          ) : (
            <PlotlyChart data={dailyChart.traces} layout={dailyChart.layout} />
          )}
        </div>
      </Card>

      <section className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-6" title="Top Pages (30d)">
          <div className="mt-4" style={{ height: 320 }}>
            {topPagesQ.isError ? (
              <p className="p-8 text-center text-sm text-[color:var(--text-dark)]">
                Failed to load data
              </p>
            ) : (
              <PlotlyChart data={topPagesChart.traces} layout={topPagesChart.layout} />
            )}
          </div>
        </Card>
        <Card className="p-6" title="Top Events (30d)">
          <div className="mt-4" style={{ height: 320 }}>
            {topEventsQ.isError ? (
              <p className="p-8 text-center text-sm text-[color:var(--text-dark)]">
                Failed to load data
              </p>
            ) : (
              <PlotlyChart data={topEventsChart.traces} layout={topEventsChart.layout} />
            )}
          </div>
        </Card>
      </section>

      <section className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-6" title="Most-Opened Documents (30d)">
          <div className="mt-4" style={{ height: 320 }}>
            {topDocumentsQ.isError ? (
              <p className="p-8 text-center text-sm text-[color:var(--text-dark)]">
                Failed to load data
              </p>
            ) : (
              <PlotlyChart data={topDocumentsChart.traces} layout={topDocumentsChart.layout} />
            )}
          </div>
        </Card>
        <Card className="p-6" title="Most-Selected Countries (30d)">
          <div className="mt-4" style={{ height: 320 }}>
            {topCountriesQ.isError ? (
              <p className="p-8 text-center text-sm text-[color:var(--text-dark)]">
                Failed to load data
              </p>
            ) : (
              <PlotlyChart data={topCountriesChart.traces} layout={topCountriesChart.layout} />
            )}
          </div>
        </Card>
      </section>

      <Card
        className="p-6"
        title="User Activity"
        actions={
          <input
            type="text"
            value={searchInput.value}
            onChange={(e) => searchInput.onChange(e.target.value)}
            placeholder="Search by email..."
            className="rounded-md border border-[color:var(--gray-200)] px-3 py-1.5 text-sm font-[var(--font-body)] text-[color:var(--text-dark)]"
          />
        }
      >
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full border-collapse text-sm font-[var(--font-body)]">
            <thead>
              <tr className="bg-[var(--light-bg)]">
                {COLUMNS.map((col) => {
                  const active = col.sortable && sortBy === col.key;
                  return (
                    <th
                      key={col.key}
                      scope="col"
                      className={`border-b border-[color:var(--gray-200)] px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                        active ? 'text-[color:var(--accent)]' : 'text-[color:var(--text-dark)]'
                      } ${col.sortable ? 'cursor-pointer select-none hover:bg-[var(--accent-light)]' : ''}`}
                      onClick={() => {
                        if (col.sortable) onSort(col.key as ActivityUsersSortKey);
                      }}
                    >
                      {col.label}
                      {active ? (order === 'asc' ? ' \u25B2' : ' \u25BC') : ''}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {usersQ.isLoading ? (
                <LoaderRow colSpan={6} />
              ) : usersQ.isError ? (
                <LoaderRow colSpan={6} message="Failed to load users" tone="error" />
              ) : usersQ.data && usersQ.data.users.length > 0 ? (
                usersQ.data.users.map((u) => (
                  <tr
                    key={u.email}
                    className="border-b border-[color:var(--gray-200)] hover:bg-[var(--light-bg)]"
                  >
                    <td className="px-4 py-2 text-[color:var(--text-dark)]">{u.email}</td>
                    <td className="px-4 py-2 text-[color:var(--text-dark)]">
                      {formatNumber(u.event_count)}
                    </td>
                    <td className="px-4 py-2 text-[color:var(--text-dark)]">
                      {formatNumber(u.page_views)}
                    </td>
                    <td className="px-4 py-2 text-[color:var(--text-dark)]">
                      {formatNumber(u.doc_views)}
                    </td>
                    <td className="px-4 py-2 text-[color:var(--text-dark)]">
                      {formatDateTime(u.last_active)}
                    </td>
                    <td className="px-4 py-2">
                      <Button size="sm" variant="primary" onClick={() => setSelectedEmail(u.email)}>
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <LoaderRow colSpan={6} message="No users found" />
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page + 1}
          totalPages={totalPages}
          onChange={(p) => update({ page: Math.max(0, p - 1) })}
        />
      </Card>

      <Modal
        open={selectedEmail !== null}
        onClose={() => setSelectedEmail(null)}
        title={selectedEmail ?? 'User Details'}
        size="lg"
      >
        {userDetailQ.isLoading ? (
          <p className="py-8 text-center text-sm text-[color:var(--text-dark)]">Loading...</p>
        ) : userDetailQ.isError || !userDetailQ.data ? (
          <p className="py-8 text-center text-sm text-red-600">Failed to load user details</p>
        ) : (
          <ActivityUserDetailModalBody detail={userDetailQ.data} />
        )}
      </Modal>
    </div>
  );
}
